import { useCallback, useEffect, useMemo, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { Tecnica } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Input,
  Space,
  Switch,
  Table,
} from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import AppSearch from "../../common/AppSearch";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";
import { debounce } from "lodash";

export default function TecnicheList() {
  const { showToast } = useToasts();
  const [tecniche, setTecniche] = useState<Tecnica[]>();
  const [error, setError] = useState(false);
  const [totalTecniche, setTotalTecniche] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [addTecnicaModal, setAddTecnicaModal] = useState(false);
  const [deleteTecnicaModal, setDeleteTecnicaModal] = useState(false);
  const [tecnicaForm] = Form.useForm();
  const [selectedTecnica, setSelectedTecnica] = useState<Tecnica>();

  const initTecniche = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    Entities.getPaginatedTecniche(urlParams).then(
      ({ data }) => {
        setTecniche((data as PagedResults<Tecnica>).items);
        setTotalTecniche((data as PagedResults<Tecnica>).totalItems);
      },
      () => {
        showToast(
          "tecniche-error",
          "Impossibile ottenere la lista delle tecniche",
          "",
          "danger"
        );
        setTecniche([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setTecniche(undefined);
    initTecniche();
  }, [initTecniche]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleEditTecnicaClick = useCallback(
    (tecnica?: Tecnica) => () => {
      setSelectedTecnica(tecnica);
      setAddTecnicaModal(true);
    },
    []
  );

  const handleFilterChange = useCallback((text: string) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: 1,
      freeText: text,
    }));
  }, []);

  const debounceSearchChange = useMemo(
    () => debounce(handleFilterChange, 500),
    [handleFilterChange]
  );

  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    };
  }, [debounceSearchChange]);

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Tecniche"
        count={totalTecniche}
        actions={
          <button
            onClick={handleEditTecnicaClick()}
            className="button-primary"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Tecnica</span>
          </button>
        }
      >
        <AppSearch
          placeholder="Cerca per nome..."
          onChange={debounceSearchChange}
        />
      </ListPageHeader>
    );
  }, [handleEditTecnicaClick, debounceSearchChange, totalTecniche]);

  const handleDeleteTecnicaClick = useCallback(
    (tecnica: Tecnica) => () => {
      setSelectedTecnica(tecnica);
      setDeleteTecnicaModal(true);
    },
    []
  );

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nessuna Tecnica"
      />
    );
  };

  const renderTecniche = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={tecniche}
        rowKey="id"
        locale={{
          emptyText: renderEmpty(),
        }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalTecniche,
        }}
      >
        <Table.Column
          key="name"
          dataIndex="name"
          title="Nome Tecnica"
          className="text-center col-pattern"
        />
        <Table.Column
          key="includeXReps"
          dataIndex="includeXReps"
          title="X-Reps"
          className="text-center col-pattern"
          render={(v: boolean) =>
            v ? (
              <span style={{ color: "#389e0d", fontWeight: 600, fontSize: 13 }}>✓ Sì</span>
            ) : (
              <span style={{ color: "#bfbfbf", fontSize: 13 }}>No</span>
            )
          }
        />
        <Table.Column
          key="dropXReps"
          dataIndex="dropXReps"
          title="Drop X-Reps"
          className="text-center col-pattern"
          render={(v: boolean) =>
            v ? (
              <span style={{ color: "#389e0d", fontWeight: 600, fontSize: 13 }}>✓ Sì</span>
            ) : (
              <span style={{ color: "#bfbfbf", fontSize: 13 }}>No</span>
            )
          }
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: Tecnica) => (
            <Space size={1}>
              <IconButton
                tooltip="Modifica"
                onClick={handleEditTecnicaClick(record)}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteTecnicaClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    tecniche,
    error,
    filters,
    handlePageChange,
    handlePageSizeChange,
    totalTecniche,
    handleEditTecnicaClick,
    handleDeleteTecnicaClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!tecniche}>{renderTecniche()}</ListPanel>;
  }, [tecniche, renderTecniche]);

  const handleAddTecnicaModalClose = useCallback(() => {
    setAddTecnicaModal(false);
    tecnicaForm.resetFields();
    setSelectedTecnica(undefined);
  }, [tecnicaForm]);

  const handleDeleteTecnicaModalClose = useCallback(() => {
    setDeleteTecnicaModal(false);
    setSelectedTecnica(undefined);
  }, []);

  const handleEditTecnicaSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedTecnica
        ? Entities.updateTecnica(selectedTecnica.id!, body)
        : Entities.createTecnica(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "tecnica-success",
            `Tecnica ${
              selectedTecnica ? "Modificata" : "Creata"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddTecnicaModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "tecnica-error",
            `Errore nel${selectedTecnica ? "la" : ""} ${
              selectedTecnica ? "Modifica" : "Salvataggio"
            } della Tecnica`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedTecnica, showToast, handleAddTecnicaModalClose, reset]
  );

  const handleDeleteTecnica = useCallback(() => {
    setLoading(true);

    Entities.deleteTecnica(selectedTecnica!.id!).then(
      () => {
        setLoading(false);
        handleDeleteTecnicaModalClose();
        reset();
        showToast(
          "delete-tecnica-success",
          "Tecnica correttamente eliminata",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-tecnica-error",
          "Errore nella cancellazione della Tecnica, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedTecnica, showToast, handleDeleteTecnicaModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal show={addTecnicaModal} onHide={handleAddTecnicaModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedTecnica ? "Modifica" : "Nuova"} Tecnica
          </Modal.Title>
        </Modal.Header>
        <Form
          form={tecnicaForm}
          layout="vertical"
          onFinish={handleEditTecnicaSubmit}
          initialValues={selectedTecnica || {}}
        >
          <Modal.Body>
            <Form.Item
              name="name"
              label="Nome Tecnica"
              rules={[{ required: true, message: "Inserisci il nome" }]}
            >
              <Input placeholder="Inserisci il nome della tecnica..." />
            </Form.Item>
            <Form.Item
              name="includeXReps"
              label="Include X-Reps"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="dropXReps"
              label="Drop X-Reps"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              variant="light"
              size="sm"
              className="border"
              onClick={handleAddTecnicaModalClose}
            >
              Chiudi
            </Button>
            <Button
              variant="success"
              size="sm"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <TailSpin width={20} height={20} />
              ) : (
                <span>
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                  Salva
                </span>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={deleteTecnicaModal} onHide={handleDeleteTecnicaModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Tecnica</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare la Tecnica{" "}
            <strong>{selectedTecnica?.name}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteTecnicaModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteTecnica}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
